import React from "react";
import { withTheme } from "@emotion/react";
import ReactApexChart from "react-apexcharts";

import { CircularProgress, Stack, Box } from "@mui/material";
import colors from "../../../../../../utils/colors";
import { withApexChartFont, apexNoOfYAxisTitle } from "../../../../../../utils/apexChartDefaults";

import useBMConfig from "../../../../../../hooks/user/useBMConfig";

const BotScoreStatsChart = ({ setCustomRangeConfirm, setTimeRange, setCustomDateRange }) => {
  const { botScoreStats } = useBMConfig();
  const [data, setData] = React.useState([
    {
      name: "Bad Bots",
      data: [],
    },
    {
      name: "Likely Bad Bots",
      data: [],
    },
    {
      name: "Likely Human",
      data: [],
    },
    {
      name: "Verified Bots",
      data: [],
    },
    {
      name: "Unknown",
      data: [],
    },
  ]);

  const [options, setOptions] = React.useState(() =>
    withApexChartFont({
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    xaxis: {
      type: "datetime",
      categories: [],
      labels: {
        datetimeUTC: false,
        style: { fontSize: "12px", colors: "#696969" },
      },
    },
    yaxis: {
      tickAmount: 5,
      title: apexNoOfYAxisTitle("No. of Events"),
      labels: {
        style: { colors: "#696969", fontSize: "12px" },
      },
    },
    tooltip: {
      x: {
        format: "dd/MM/yy HH:mm",
      },
    },
    chart: {
      stacked: true,
      zoom: {
        enabled: true,
      },
      type: "bar",
      toolbar: { show: false },
    },
    colors: ["#873CDF", "#1E89a5", "#4b3adc", "#369F33", "#E8528a"],
    legend: { show: true, fontSize: "12px" },
  })
  );

  React.useEffect(() => {
    let categories = [];
    const goodBotDatas = [];
    const badBotDatas = [];
    const BADBotDatas = [];
    const humanDatas = [];
    const unknownDatas = [];
    if (botScoreStats?.length > 0) {
      botScoreStats.forEach((stat) => {
        const key = stat?.key_as_string;
        if (key && !Number.isNaN(new Date(key).getTime())) {
          categories.push(key);
        }
      });
    }
    // Remove duplicated entries
    categories = Array.from(new Set(categories));
    categories.sort();
    if (categories.length > 0) {
      categories.forEach((cate) => {
        if (null === botScoreStats || 0 === botScoreStats.length) {
          goodBotDatas.push(0);
          badBotDatas.push(0);
          BADBotDatas.push(0);
          humanDatas.push(0);
          unknownDatas.push(0);
        } else {
          const sig = botScoreStats.find((x) => x?.key_as_string === cate);
          const stats = sig?.stats || {};
          const good = Number(stats.good) || 0;
          const bad = Number(stats.bad) || 0;
          const veryBad = Number(stats.BAD) || 0;
          const human = Number(stats.human) || 0;
          const unknown = Number(stats.unknown) || 0;
          const total = Number(stats.total) || 0;

          const detailedSum = good + bad + veryBad + human + unknown;
          const shouldFallbackToTotal = detailedSum === 0 && total > 0;
          goodBotDatas.push(good);
          badBotDatas.push(bad);
          BADBotDatas.push(veryBad);
          humanDatas.push(human);
          unknownDatas.push(shouldFallbackToTotal ? total : unknown);
        }
      });
    }
    setData([
      {
        name: "Bad Bots",
        data: BADBotDatas,
      },
      {
        name: "Likely Bad Bots",
        data: badBotDatas,
      },
      {
        name: "Likely Human",
        data: humanDatas,
      },
      {
        name: "Verified Bots",
        data: goodBotDatas,
      },
      {
        name: "Unknown",
        data: unknownDatas,
      },
    ]);
    setOptions(
      withApexChartFont({
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      yaxis: {
        tickAmount: 5,
        labels: {
          style: { colors: "#696969", fontSize: "12px" },
        },
      },
      xaxis: {
        type: "datetime",
        categories: categories,
        labels: {
          datetimeUTC: false,
          style: { fontSize: "12px", colors: "#696969" },
        },
      },
      tooltip: {
        x: {
          format: "dd/MM/yy HH:mm",
        },
      },
      chart: {
        stacked: true,
        zoom: {
          enabled: true,
        },
        toolbar: { show: false },
        type: "bar",
        events: {
          zoomed: function (chartContext, zoomPayload = {}) {
            const xaxis = zoomPayload?.xaxis || {};
            if (xaxis.min === undefined || xaxis.max === undefined) {
              setTimeRange("1d");
            } else {
              const from = new Date(xaxis.min);
              const to = new Date(xaxis.max);
              const month = String(from.getMonth() + 1).padStart(2, "0");
              const date = String(from.getDate()).padStart(2, "0");
              const year = String(from.getFullYear());
              const hour = String(from.getHours()).padStart(2, "0");
              const minute = String(from.getMinutes()).padStart(2, "0");
              const second = String(from.getSeconds()).padStart(2, "0");
              const _month = String(to.getMonth() + 1).padStart(2, "0");
              const _date = String(to.getDate()).padStart(2, "0");
              const _year = String(to.getFullYear());
              const _hour = String(to.getHours()).padStart(2, "0");
              const _minute = String(to.getMinutes()).padStart(2, "0");
              const _second = String(to.getSeconds()).padStart(2, "0");
              setTimeRange("custom");
              setCustomRangeConfirm(true);
              setCustomDateRange([
                year + "-" + month + "-" + date + "T" + hour + ":" + minute + ":" + second,
                _year + "-" + _month + "-" + _date + "T" + _hour + ":" + _minute + ":" + _second,
              ]);
            }
          },
        },
      },
      colors: ["#873CDF", "#1E89a5", "#4b3adc", "#369F33", "#E8528a"],
      legend: { show: true, fontSize: "12px" },
    })
    );
  }, [botScoreStats, setTimeRange, setCustomDateRange, setCustomRangeConfirm]);

  return (
    <>
      {null === botScoreStats ? (
        <Stack alignItems={"center"} justifyContent={"center"} sx={{ height: "100%" }}>
          <CircularProgress sx={{ color: colors.neutral[400] }} />
        </Stack>
      ) : (
        <Box sx={{ width: "100%", pr: 3 }}>
          <ReactApexChart options={options} series={data} type="bar" height="250" />
        </Box>
      )}
    </>
  );
};

export default withTheme(BotScoreStatsChart);
